<template>
  <no-data-wrapper :has-data="hasData">
    <line-chart :data="chartData" :options="chartOptions" style="height: 100%" />
  </no-data-wrapper>
</template>

<i18n lang="yaml">
ja:
  day_format: 'M月d日'

en:
  day_format: 'MMM d'
</i18n>

<script>
import { parse, setDate } from 'date-fns';
import { merge } from 'lodash';

import { datesOfMonth } from '@/libs/time';
import dateTimeFormatting from '@/mixins/dateTimeFormatting';

import LineChart from '@/components/atoms/LineChart';
import NoDataWrapper from '../NoDataWrapper';
import { dailyInsectCounts, dailyAlerts } from '../chartCalculations';

export const ALERT_STYLES = {
  totalCountAlert: {
    backgroundColor: '#edc09d',
    borderColor: '#edc09d',
  },
  increaseCountAlert: {
    backgroundColor: 'rgba(0, 0, 0, 0)',
    borderColor: 'crimson',
    borderWidth: 3,
  },
};

// TODO: Add alert points as proper legend
//   - How? Fake empty data set? Put alert points in data set?

export default {
  name: 'daily-insect-count-chart',
  mixins: [dateTimeFormatting],
  components: {
    LineChart,
    NoDataWrapper,
  },
  props: {
    // { chartInfo: { alert: boolean, count: int, increaseAlert: boolean }, timestamp: int }
    events: {
      type: Array,
      required: true,
    },
    // { month: 'YYYY-MM', color: HTMLColor, label: str }
    graphs: {
      type: Array,
      required: true,
    },
    yMax: {
      type: Number,
      required: true,
    },
    showAlertPeriods: {
      type: Boolean,
      default: false,
    },
    onChartClick: {
      type: Function,
      default: null,
    },
  },
  computed: {
    hasData() {
      if (!this.events || this.events.length === 0) {
        return false;
      }
      // グラフに表示するデータが存在するかチェック
      return this.chartInsectCountDatasets.some(dataset =>
        dataset.data.some(value => value !== null && value >= 0));
    },
    chartData() {
      return {
        labels: this.chartXAxisLabels,
        datasets: this.chartInsectCountDatasets,
      };
    },
    chartInsectCountDatasets() {
      return this.graphs.map(graph => {
        const dates = datesOfMonth(graph.month);

        const style = {
          pointBackgroundColor: graph.color,
          pointBorderColor: graph.color,
          pointBorderWidth: 2,
        };
        if (this.showAlertPeriods) {
          const alerts = dailyAlerts(dates, this.events);
          style.pointBackgroundColor = alerts.map(alert =>
            (alert.total ? ALERT_STYLES.totalCountAlert.backgroundColor : graph.color));
          style.pointBorderColor = alerts.map(alert =>
            (alert.increase ? ALERT_STYLES.increaseCountAlert.borderColor : graph.color));
          style.pointRadius = alerts.map(alert =>
            (alert.total || alert.increase ? 5 : 3));
        }

        return {
          label: graph.label,
          data: dailyInsectCounts(dates, this.events),
          borderColor: graph.color,
          backgroundColor: graph.color,
          tension: 0, // straight lines
          ...style,
        };
      });
    },
    chartOptions() {
      return {
        plugins: {
          legend: {
            labels: {
              boxWidth: 12,
              boxHeight: 4,
            },
          },
          tooltip: {
            callbacks: {
              title: (context) => context.map(this.tooltipTitle),
            },
          },
        },
        scales: {
          xAxis: this.chartXAxis,
          yAxis: this.chartYAxis,
        },
        onClick: (event, activeElements) => {
          if (activeElements.length > 0 && this.onChartClick) {
            this.handleChartClick(activeElements[0]);
          }
        },
      };
    },
    chartXAxisLabels() {
      return this.monthDates.map(date => this.formatLocalized(date, this.$t('day_format')));
    },
    chartXAxis() {
      return this.buildAxis();
    },
    chartYAxis() {
      let stepSize = 5;
      if (this.yMax > 100) {
        stepSize = 20;
      }

      return this.buildAxis(
        {
          beginAtZero: true,
          suggestedMax: this.yMax,
          ticks: {
            stepSize,
          },
        },
      );
    },
    labelForDate() {
      return (date) => {
        const dateIndex = this.monthDates.indexOf(date);
        return this.chartXAxisLabels[dateIndex];
      };
    },
    month() {
      return this.graphs[0].month;
    },
    monthDates() {
      return datesOfMonth(this.month);
    },
  },
  methods: {
    annotationStyle(type) {
      switch (type) {
        case 'cumulative':
          return ALERT_STYLES.totalCountAlert;
        case 'differential':
          return ALERT_STYLES.increaseCountAlert;
        default:
          return {};
      }
    },
    buildAxis(options) {
      return merge(
        {
          grid: {
            borderColor: 'rgb(102, 102, 102)',
            display: false,
            drawTicks: false,
          },
          ticks: {
            padding: 10,
          },
        },
        options,
      );
    },
    emitMaxCount() {
      const datasetMaxes = this.chartInsectCountDatasets.map(({ data }) => Math.max(...data));
      this.$emit('max-count-updated', Math.max(...datasetMaxes));
    },
    handleChartClick(clickedElement) {
      const { datasetIndex } = clickedElement;
      const dataIndex = clickedElement.index;
      const graph = this.graphs[datasetIndex];
      const clickedDate = setDate(parse(graph.month, 'yyyy-MM', new Date()), dataIndex + 1);
      const dateString = this.formatDateForApi(clickedDate);
      this.onChartClick(dateString);
    },
    tooltipTitle(context) {
      const graph = this.graphs[context.datasetIndex];
      const date = setDate(parse(graph.month, 'yyyy-MM', new Date()), context.dataIndex + 1);
      return this.formatLocalized(date, this.$t('day_format'));
    },
  },
  mounted() {
    this.emitMaxCount();
  },
  watch: {
    chartInsectCountDatasets() {
      this.emitMaxCount();
    },
  },
};
</script>
